import type { SupabaseClient } from '@supabase/supabase-js';
import { authConfig } from './config';

const TRANSACTION_KEY = 'bubbleteaworld-central-sso-transaction';
const POPUP_RELAY_MARKER_KEY = 'bubbleteaworld-central-sso-popup-relay-marker';
const CALLBACK_PATH = '/auth/callback';
const CALLBACK_MESSAGE_TYPE = 'bubbleteaworld-central-sso-popup-callback-v1';
const CALLBACK_ACK_TYPE = 'bubbleteaworld-central-sso-popup-callback-ack-v1';
const REQUEST_TIMEOUT_MS = 15_000;
const TRANSACTION_MAX_AGE_MS = 10 * 60 * 1_000;
const POPUP_RETRY_MS = 300;
const POPUP_ACK_TIMEOUT_MS = 15_000;

let callbackCompletionPromise: Promise<boolean> | null = null;

type Transaction = {
  state: string;
  codeVerifier: string;
  nonce: string;
  redirectUri: string;
  createdAt: number;
};

type CallbackResponse = {
  code?: string;
  state?: string;
  error?: string;
};

type TokenResponse = {
  access_token?: unknown;
  refresh_token?: unknown;
};

export type CentralSsoErrorCode =
  | 'central_sso_not_configured'
  | 'central_sso_issuer_mismatch'
  | 'central_sso_storage_failed'
  | 'central_sso_state_mismatch'
  | 'central_sso_missing_callback'
  | 'central_sso_token_exchange_failed'
  | 'central_sso_invalid_token_response'
  | 'central_sso_session_failed'
  | 'central_sso_popup_blocked'
  | 'central_sso_popup_failed'
  | 'central_sso_popup_closed'
  | 'central_sso_popup_timeout'
  | 'access_denied'
  | 'invalid_request'
  | 'invalid_client'
  | 'invalid_redirect_uri'
  | 'invalid_grant'
  | 'pkce_verification_failed';

export class CentralSsoError extends Error {
  constructor(public readonly code: CentralSsoErrorCode) {
    super(code);
    this.name = 'CentralSsoError';
  }
}

export type CentralSsoPopupCallbackMessage = {
  type: typeof CALLBACK_MESSAGE_TYPE;
  callback: CallbackResponse;
};

export type CentralSsoPopupAttempt = {
  popup: Window;
  ready: Promise<void>;
};

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return encodeBase64Url(bytes);
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return encodeBase64Url(new Uint8Array(digest));
}

function getConfig() {
  const issuerUrl = authConfig.centralAuthIssuerUrl.replace(/\/$/, '');
  const clientId = authConfig.centralAuthClientId.trim();
  const redirectUri = authConfig.centralAuthRedirectUri.trim();
  const supabaseUrl = authConfig.supabaseUrl.replace(/\/$/, '');

  if (!supabaseUrl || !authConfig.publishableKey || !issuerUrl || !clientId || !redirectUri) {
    throw new CentralSsoError('central_sso_not_configured');
  }
  if (issuerUrl !== supabaseUrl) {
    throw new CentralSsoError('central_sso_issuer_mismatch');
  }

  return { issuerUrl, clientId, redirectUri, scope: authConfig.centralAuthScope };
}

function isCallbackPath(): boolean {
  return window.location.pathname === CALLBACK_PATH;
}

function getCallbackResponse(): CallbackResponse {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  return {
    ...(code ? { code } : {}),
    ...(state ? { state } : {}),
    ...(error ? { error } : {}),
  };
}

export function hasCentralSsoCallbackResponse(): boolean {
  if (!isCallbackPath()) {
    return false;
  }
  const response = getCallbackResponse();
  return Boolean(response.code || response.state || response.error);
}

function scrubCallbackUrl(): void {
  window.history.replaceState({}, '', window.location.pathname);
}

function saveTransaction(transaction: Transaction): void {
  try {
    window.sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify(transaction));
  } catch {
    throw new CentralSsoError('central_sso_storage_failed');
  }
}

function readTransaction(): Transaction | null {
  try {
    const raw = window.sessionStorage.getItem(TRANSACTION_KEY);
    if (!raw) {
      return null;
    }
    const transaction = JSON.parse(raw) as Partial<Transaction>;
    if (
      typeof transaction.state !== 'string'
      || typeof transaction.codeVerifier !== 'string'
      || typeof transaction.nonce !== 'string'
      || typeof transaction.redirectUri !== 'string'
      || typeof transaction.createdAt !== 'number'
    ) {
      return null;
    }
    return transaction as Transaction;
  } catch {
    return null;
  }
}

export function clearCentralSsoTransaction(): void {
  try {
    window.sessionStorage.removeItem(TRANSACTION_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

async function createAuthorizeUrl(): Promise<string> {
  const { issuerUrl, clientId, redirectUri, scope } = getConfig();
  const state = randomBase64Url(32);
  const codeVerifier = randomBase64Url(48);
  const nonce = randomBase64Url(32);

  try {
    const codeChallenge = await createCodeChallenge(codeVerifier);
    saveTransaction({ state, codeVerifier, nonce, redirectUri, createdAt: Date.now() });
    const authorizeUrl = new URL(`${issuerUrl}/auth/v1/oauth/authorize`);
    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('client_id', clientId);
    authorizeUrl.searchParams.set('redirect_uri', redirectUri);
    authorizeUrl.searchParams.set('code_challenge', codeChallenge);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('scope', scope);
    if (scope.split(/\s+/).includes('openid')) {
      authorizeUrl.searchParams.set('nonce', nonce);
    }
    return authorizeUrl.toString();
  } catch (error) {
    if (error instanceof CentralSsoError) {
      throw error;
    }
    throw new CentralSsoError('central_sso_storage_failed');
  }
}

export function startCentralSsoPopup(): CentralSsoPopupAttempt {
  let popup: Window | null = null;
  try {
    popup = window.open('about:blank', '_blank', 'popup=yes,width=480,height=720');
  } catch {
    throw new CentralSsoError('central_sso_popup_blocked');
  }
  if (!popup) {
    throw new CentralSsoError('central_sso_popup_blocked');
  }

  try {
    popup.sessionStorage.setItem(POPUP_RELAY_MARKER_KEY, randomBase64Url(32));
  } catch {
    popup.close();
    throw new CentralSsoError('central_sso_storage_failed');
  }

  const ready = createAuthorizeUrl()
    .then((authorizeUrl) => {
      if (popup?.closed) {
        throw new CentralSsoError('central_sso_popup_closed');
      }
      popup?.location.replace(authorizeUrl);
    })
    .catch((error: unknown) => {
      clearCentralSsoTransaction();
      popup?.close();
      throw error instanceof CentralSsoError ? error : new CentralSsoError('central_sso_popup_failed');
    });

  return { popup, ready };
}

export function isCentralSsoPopupCallbackMessage(value: unknown): value is CentralSsoPopupCallbackMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const message = value as Record<string, unknown>;
  if (message.type !== CALLBACK_MESSAGE_TYPE || !message.callback || typeof message.callback !== 'object') {
    return false;
  }
  const callback = message.callback as Record<string, unknown>;
  return Object.keys(message).every((key) => key === 'type' || key === 'callback')
    && Object.keys(callback).every((key) => key === 'code' || key === 'state' || key === 'error')
    && (callback.code === undefined || typeof callback.code === 'string')
    && (callback.state === undefined || typeof callback.state === 'string')
    && (callback.error === undefined || typeof callback.error === 'string')
    && (typeof callback.code === 'string' || typeof callback.state === 'string' || typeof callback.error === 'string');
}

function mapOAuthError(error: string | undefined): CentralSsoErrorCode {
  switch (error) {
    case 'access_denied': return 'access_denied';
    case 'invalid_client': return 'invalid_client';
    case 'invalid_redirect_uri': return 'invalid_redirect_uri';
    case 'invalid_grant': return 'invalid_grant';
    case 'pkce_verification_failed': return 'pkce_verification_failed';
    default: return 'invalid_request';
  }
}

async function exchangeCode(issuerUrl: string, clientId: string, transaction: Transaction, code: string): Promise<TokenResponse> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${issuerUrl}/auth/v1/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: transaction.redirectUri,
        code_verifier: transaction.codeVerifier,
      }),
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new CentralSsoError('central_sso_token_exchange_failed');
    }
    return await response.json() as TokenResponse;
  } catch (error) {
    throw error instanceof CentralSsoError
      ? error
      : new CentralSsoError('central_sso_token_exchange_failed');
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function completeCallback(client: SupabaseClient, response: CallbackResponse): Promise<boolean> {
  const transaction = readTransaction();
  if (!response.state || !transaction) {
    clearCentralSsoTransaction();
    throw new CentralSsoError('central_sso_missing_callback');
  }
  if (response.state !== transaction.state) {
    clearCentralSsoTransaction();
    throw new CentralSsoError('central_sso_state_mismatch');
  }
  const config = getConfig();
  if (transaction.redirectUri !== config.redirectUri) {
    clearCentralSsoTransaction();
    throw new CentralSsoError('invalid_redirect_uri');
  }
  if (Date.now() - transaction.createdAt > TRANSACTION_MAX_AGE_MS) {
    clearCentralSsoTransaction();
    throw new CentralSsoError('invalid_grant');
  }
  if (response.error) {
    clearCentralSsoTransaction();
    throw new CentralSsoError(mapOAuthError(response.error));
  }
  if (!response.code) {
    clearCentralSsoTransaction();
    throw new CentralSsoError('central_sso_missing_callback');
  }

  try {
    const tokenResponse = await exchangeCode(config.issuerUrl, config.clientId, transaction, response.code);
    if (typeof tokenResponse.access_token !== 'string' || typeof tokenResponse.refresh_token !== 'string') {
      throw new CentralSsoError('central_sso_invalid_token_response');
    }
    const { error } = await client.auth.setSession({
      access_token: tokenResponse.access_token,
      refresh_token: tokenResponse.refresh_token,
    });
    if (error) {
      throw new CentralSsoError('central_sso_session_failed');
    }
  } finally {
    clearCentralSsoTransaction();
  }
  return true;
}

export async function completeCentralSsoPopupCallback(
  client: SupabaseClient,
  message: CentralSsoPopupCallbackMessage,
): Promise<boolean> {
  return completeCallback(client, message.callback);
}

export function completeCentralSsoCallbackOnce(client: SupabaseClient): Promise<boolean> {
  if (callbackCompletionPromise) {
    return callbackCompletionPromise;
  }
  if (!hasCentralSsoCallbackResponse()) {
    return Promise.resolve(false);
  }
  const response = getCallbackResponse();
  scrubCallbackUrl();
  callbackCompletionPromise = completeCallback(client, response);
  return callbackCompletionPromise;
}

export function relayCentralSsoPopupCallback(): boolean {
  if (!hasCentralSsoCallbackResponse() || !window.opener || window.opener === window) {
    return false;
  }
  try {
    const marker = window.sessionStorage.getItem(POPUP_RELAY_MARKER_KEY);
    window.sessionStorage.removeItem(POPUP_RELAY_MARKER_KEY);
    if (!marker) {
      return false;
    }
  } catch {
    return false;
  }

  const response = getCallbackResponse();
  scrubCallbackUrl();
  const message: CentralSsoPopupCallbackMessage = { type: CALLBACK_MESSAGE_TYPE, callback: response };
  let retryId: number | null = null;
  let timeoutId: number | null = null;
  const cleanup = () => {
    if (retryId !== null) window.clearInterval(retryId);
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    window.removeEventListener('message', acknowledge);
  };
  const acknowledge = (event: MessageEvent<unknown>) => {
    const data = event.data as { type?: unknown; state?: unknown } | null;
    if (
      event.origin !== window.location.origin
      || event.source !== window.opener
      || data?.type !== CALLBACK_ACK_TYPE
      || data.state !== (response.state ?? '')
    ) {
      return;
    }
    cleanup();
    window.close();
  };
  const relay = () => window.opener?.postMessage(message, window.location.origin);
  window.addEventListener('message', acknowledge);
  relay();
  retryId = window.setInterval(relay, POPUP_RETRY_MS);
  timeoutId = window.setTimeout(() => {
    cleanup();
    (document.getElementById('root') ?? document.body).textContent = '登入回應未完成，請關閉此視窗後重試。';
  }, POPUP_ACK_TIMEOUT_MS);
  return true;
}

export function acknowledgeCentralSsoPopupCallback(popup: Window, state: string): void {
  popup.postMessage({ type: CALLBACK_ACK_TYPE, state }, window.location.origin);
}
